import Swiper from "swiper";
import { FreeMode, Navigation } from "swiper/modules";

/**
 * ui-tabs 가로 탭 바를 Swiper(FreeMode + Navigation)로 감싼다.
 * 탭이 화면 폭을 넘쳐 잘리던 문제를 드래그/스와이프 + 좌우 화살표로 해결.
 *
 * Opt-in: `data-tabs-swiper` 속성이 붙은 `.ui-tabs` 에만 적용.
 * (ui-tabs 는 여러 페이지가 공유하므로 전체 적용 금지 — pill/toggle/쇼케이스 변형 보호)
 *
 * 레일(rail) 구조 — 화살표를 탭 항목과 같은 평면에 겹치지 않게:
 *   <div class="ui-tabs-rail">
 *     <button class="ui-tabs-rail__arrow ui-tabs-rail__arrow--prev">
 *     <nav class="ui-tabs swiper"> …swiper viewport… </nav>
 *     <button class="ui-tabs-rail__arrow ui-tabs-rail__arrow--next">
 *   </div>
 * 화살표는 `.ui-tabs` 의 형제(rail 직계 자식)라 스크롤 viewport 바깥 별도 칸에 위치한다.
 *
 * SubNavSwiper 와 동일한 swiper 패턴이나, ui-tabs 는 내부 list 가 없는 평면 구조
 * (`nav.ui-tabs > a.ui-tabs__item`)라 `.swiper-wrapper` div 를 생성해 항목을 옮긴다.
 */
export class UiTabsSwiper {
  static init(): void {
    const navs = document.querySelectorAll<HTMLElement>(
      ".ui-tabs[data-tabs-swiper]",
    );

    navs.forEach((navEl) => {
      const items = [
        ...navEl.querySelectorAll<HTMLElement>(".ui-tabs__item"),
      ];
      if (!items.length) return;

      // 이미 초기화된 탭 바는 재초기화에서 건너뛴다(rail/wrapper 중복 생성 방지).
      if (navEl.dataset.uiTabsSwiperBound === "true") return;
      navEl.dataset.uiTabsSwiperBound = "true";

      // 항목이 `<li>` 로 감싸진 경우(news/people 등)와 평면 `<a>` 구조(검색/guide)를
      // 모두 지원 — 각 항목의 "슬라이드가 될 요소"는 navEl 의 직계 자식(li 또는 a).
      const slides = items.map((item) => {
        let slide: HTMLElement = item;
        while (slide.parentElement && slide.parentElement !== navEl) {
          slide = slide.parentElement;
        }
        return slide;
      });

      // .swiper-wrapper 생성 후 슬라이드 요소를 이동 (활성 인덱스 기준점 = slides)
      const wrapper = document.createElement("div");
      wrapper.className = "swiper-wrapper";
      slides.forEach((slide) => {
        slide.classList.add("swiper-slide");
        wrapper.appendChild(slide);
      });
      navEl.appendChild(wrapper);
      navEl.classList.add("swiper");

      // 바깥 레일 셸 — `.ui-tabs` 를 감싸 화살표 전용 칸을 양옆에 둔다.
      const rail = document.createElement("div");
      rail.className = "ui-tabs-rail";
      navEl.parentNode?.insertBefore(rail, navEl);

      // prev / next 화살표 버튼 (rail 직계 자식 = `.ui-tabs` 의 형제)
      const prevBtn = document.createElement("button");
      prevBtn.type = "button";
      prevBtn.className = "ui-tabs-rail__arrow ui-tabs-rail__arrow--prev";
      prevBtn.setAttribute("aria-label", "Previous");

      const nextBtn = document.createElement("button");
      nextBtn.type = "button";
      nextBtn.className = "ui-tabs-rail__arrow ui-tabs-rail__arrow--next";
      nextBtn.setAttribute("aria-label", "Next");

      rail.appendChild(prevBtn);
      rail.appendChild(navEl);
      rail.appendChild(nextBtn);

      const swiper = new Swiper(navEl, {
        modules: [FreeMode, Navigation],
        slidesPerView: "auto",
        freeMode: { enabled: true, momentum: false },
        watchOverflow: true,
        // 초기 숨김(0-width) 탭 패널 안에서 마운트돼도 노출/리사이즈 시 self-heal.
        observer: true,
        observeParents: true,
        resizeObserver: true,
        navigation: { prevEl: prevBtn, nextEl: nextBtn },
      });

      // 활성 탭 위치로 이동 (is-active 는 __item 에 부여 — 슬라이드 인덱스로 환산)
      const activeIndex = slides.findIndex((slide) =>
        slide.querySelector(".ui-tabs__item.is-active") ||
        slide.classList.contains("is-active"),
      );
      if (activeIndex > 0) swiper.slideTo(activeIndex, 0);
    });
  }
}
